import React, { useState } from 'react';
import { CheckboxField, TextField } from '@kaizen/draft-form';
import { SelectOption, CheckedOption } from '../../types';
import { OTHER_OPTION_ID } from '../../constants';
import selectQuestionStyles from '../SelectQuestion.scss';

export type Props = {
  questionId: string;
  questionHeaderId: string;
  questionLimitId?: string;
  questionText: string;
  options: SelectOption[];
  existingOtherValue: string | null;
  existingSelections: string[];
  selectionLimit: number;
  questionMandatory: boolean;
  onSave: (
    checkedOptions: CheckedOption[],
    otherValue: string,
    checkedOption: string,
    answered: boolean
  ) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  isPDF: boolean;
  validationError: boolean;
  validationErrorId: string;
  uiText: {
    selectCount: string;
    selectionLimitError: string;
    savingError: string;
    otherLabel: string;
    forLabel: string;
  };
};

const MultiSelectCheckboxes = (props: Props): React$Element<string> => {
  const {
    questionId,
    questionHeaderId,
    questionLimitId,
    questionText,
    options,
    existingOtherValue,
    existingSelections,
    selectionLimit,
    questionMandatory,
    onSave,
    containerRef,
    isPDF,
    uiText,
    validationError,
    validationErrorId,
  } = props;
  const [checkedOptionIds, setChecked] = useState<string[]>(existingSelections);
  const otherCheckedStatus = checkedOptionIds.indexOf(OTHER_OPTION_ID) > -1;
  const [showOtherInput, setShowOtherInput] =
    useState<boolean>(otherCheckedStatus);
  const [otherValue, setOtherValue] = useState<string>(
    existingOtherValue || ''
  );

  const overSelectionLimit = (): boolean => {
    if (selectionLimit === -1 || checkedOptionIds.length < selectionLimit) {
      return false;
    }
    alert(uiText.selectionLimitError);
    return true;
  };

  const onCheck = (optionId: string, check: boolean) => {
    if (check && overSelectionLimit()) {
      return;
    }

    if (optionId === OTHER_OPTION_ID) {
      setShowOtherInput(check);
    }

    if (!check) {
      setChecked(checkedOptionIds.filter((oid) => oid !== optionId));
    } else {
      // Update state optimistically
      setChecked([...checkedOptionIds, optionId]);
    }

    // Send request
    const optionsChecked = options.map((o) => ({
      id: o.value,
      checked:
        o.value === optionId ? check : checkedOptionIds.indexOf(o.value) > -1,
    }));
    const hasAtLeastOneChecked =
      optionsChecked.find((o) => o.checked) !== undefined;
    onSave(optionsChecked, otherValue, optionId, hasAtLeastOneChecked);

    // Update the record of the selected options still living in Haml, for dependent question functionality
    $(`input[data-select-option="${optionId}"]`).val(check ? 'true' : 'false');

    // Send back to jQuery for some adjustments to the .question container that we can't make here yet
    $(document).trigger('reactAnswerChange', {
      container: containerRef.current,
      answered: hasAtLeastOneChecked,
    });
  };

  const onOtherChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtherValue(event.target.value);
  };

  const onOtherBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const optionsChecked = options.map((o) => ({
      id: o.value,
      checked: checkedOptionIds.indexOf(o.value) > -1,
    }));
    onSave(optionsChecked, event.target.value, 'text_value', true);
  };

  return (
    <div
      role="group"
      aria-labelledby={questionHeaderId}
      aria-describedby={
        [validationError && validationErrorId, questionLimitId]
          .filter(Boolean)
          .join(' ') || undefined
      }
      data-focus-to-on-navigate="true"
      tabIndex={-1}
      className={selectQuestionStyles.inputGroup}
      aria-required={questionMandatory}
    >
      {options.map((option) => {
        const checkedStatus = checkedOptionIds.indexOf(option.value) > -1;
        const showInput =
          option.value === OTHER_OPTION_ID && (showOtherInput || isPDF);
        return (
          <div
            key={option.value}
            data-automation-id={`select-option-wrapper-multi-${option.value}`}
            data-automation-selected={checkedStatus}
          >
            <CheckboxField
              id={`${questionId}-${option.value}`}
              name={option.label}
              labelText={option.label}
              onCheck={() => onCheck(option.value, !checkedStatus)}
              checkedStatus={checkedStatus ? 'on' : 'off'}
              // This aria-label _shouldn't_ be required, but there's something weird going on.
              // Windows/Chromium is having trouble determining the accessible name from the label in this environment.
              // See https://cultureamp.slack.com/archives/C0405G8EJ48/p1696896101605489 for more information.
              aria-label={option.label}
            />
            {showInput && (
              <div
                className={selectQuestionStyles.otherInputContainer}
                data-automation-id={`select-option-multi-other-input`}
              >
                <TextField
                  id={`${questionId}-other-input`}
                  labelText={uiText.otherLabel}
                  name={option.label}
                  value={otherValue}
                  onChange={onOtherChange}
                  onBlur={onOtherBlur}
                  aria-label={`${option.label} ${uiText.otherLabel} ${uiText.forLabel} ${questionText}`}
                  data-automation-id={`other-input-multi-${questionId}`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MultiSelectCheckboxes;
